import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Grid,
  Checkbox,
  Box,
  Button,
  Typography,
  Skeleton,
} from '@mui/material';
import { AppContext } from '../../context/Context';
import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import clubhouseLogo from '../../assets/img/clubhouse_logo.png';
import okBingoLogo from '../../assets/img/okbingo.png';
import wingamesLogo from '../../assets/img/wingames.png';

function TransactionList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    updateIsDataBingoBlockedInKiosk,
    updatePrintReceiptKiosk,
    printReceiptKiosk,
    updateShowModalPaymentKiosk,
    updateShowModalConfirmLogout,
    transactionsKiosk,
    logoutUser,
    logoutKiosk,
    updateUserLimits,
    userLimits,
    OKBingoID,
    withdraw,
    session,
    transactions,
    deleteTransactionsKiosk,
    operator,
    hall,
    currentUser,
    userInfo,
    payCard,
    payCardTemp,
    payCash,
    payCashTemp,
    paySpillerkonto,
    paySpillerkontoTemp,
    updatePrint,
    parentInfo,
    printer,
    updateIsTerminalBusy,
    updateIsLoadingScreen,
    updateMaxAllowed,
    updateShowIsLimitSurpassedTransaction,
    updateIsDailyLimitSurpassed,
    updateCurrentTicketOK,
    updateShowUserInfoOK,
    updateTransactions,
    updateIsLimitSurpassed,
    updateIsMonthlyLimitSurpassed,
    updateShowIsLimitSurpassed,
  } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const [isLoadingLimits, setIsLoadingLimits] = useState(true);

  const [time, setTime] = useState(() => {
    const now = new Date();
    return now.toLocaleTimeString('no-NO', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  });
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setTime(
        now.toLocaleTimeString('no-NO', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
      );
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Remove transactions whose game cutoff time has passed
  useEffect(() => {
    if (!Array.isArray(transactionsKiosk) || transactionsKiosk.length === 0)
      return;
    const now = Date.now();
    const hasExpired = transactionsKiosk.some(
      (tx) => tx.cutoffTime && now >= tx.cutoffTime
    );
    if (!hasExpired) return;
    const updated = transactionsKiosk.filter(
      (tx) => !tx.cutoffTime || now < tx.cutoffTime
    );
    deleteTransactionsKiosk(updated);
    toast.error(t('kiosk.transactionGameExpired'));
  }, [time]);

  const total =
    Array.isArray(transactionsKiosk) && transactionsKiosk.length > 0
      ? transactionsKiosk.reduce(
          (sum, transaction) => sum + (parseInt(transaction?.amount) || 0),
          0
        )
      : 0;

  useEffect(() => {
    if (
      total -
        (payCash + payCashTemp) -
        (payCard + payCardTemp) -
        (paySpillerkonto + paySpillerkontoTemp) -
        withdraw <=
      0
    ) {
      updatePrint(false);
    }
  }, [total]);

  const userLimitsRef = useRef(userLimits);
  useEffect(() => {
    userLimitsRef.current = userLimits;
  }, [userLimits]);

  const OKBingoIDRef = useRef(OKBingoID);
  useEffect(() => {
    OKBingoIDRef.current = OKBingoID;
  }, [OKBingoID]);

  const [okTicketID, setOkTicketID] = useState(false);
  const okTicketIDRef = useRef(okTicketID);
  useEffect(() => {
    okTicketIDRef.current = okTicketID;
  }, [okTicketID]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const operatorRef = useRef(operator);
  useEffect(() => {
    operatorRef.current = operator;
  }, [operator]);

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const printerRef = useRef(printer);
  useEffect(() => {
    printerRef.current = printer;
  }, [printer]);

  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const [transactionToDelete, setTransactionToDelete] = useState({});
  const transactionToDeleteRef = useRef(transactionToDelete);
  useEffect(() => {
    transactionToDeleteRef.current = transactionToDelete;
  }, [transactionToDelete]);

  const transactionsRef = useRef(transactions);
  useEffect(() => {
    transactionsRef.current = transactions;
  }, [transactions]);

  const transactionsKioskRef = useRef(transactionsKiosk);
  useEffect(() => {
    transactionsKioskRef.current = transactionsKiosk;
  }, [transactionsKiosk]);

  // Drag-to-scroll for the orders list; pointer events unify mouse and touch (and VNC)
  const listRef = useRef(null);
  const dragState = useRef({ active: false, startY: 0, startScrollTop: 0 });

  const handleListPointerDown = (event) => {
    const el = listRef.current;
    if (!el) return;
    dragState.current = {
      active: true,
      startY: event.clientY,
      startScrollTop: el.scrollTop,
    };
  };

  const handleListPointerMove = (event) => {
    const el = listRef.current;
    if (!el || !dragState.current.active) return;
    const delta = event.clientY - dragState.current.startY;
    el.scrollTop = dragState.current.startScrollTop - delta;
  };

  const endListDrag = () => {
    dragState.current.active = false;
  };

  useEffect(() => {
    const currentTransactionsKiosk = Array.isArray(transactionsKiosk)
      ? transactionsKiosk
      : [];

    const shouldBlockDataBingo = currentTransactionsKiosk.some(
      (transaction) =>
        transaction?.name === 'Ny Billett - WG' ||
        transaction?.name === 'Ny Billett - OK D.'
    );

    updateIsDataBingoBlockedInKiosk(shouldBlockDataBingo);
  }, [transactionsKiosk, updateIsDataBingoBlockedInKiosk]);

  useEffect(() => {
    const handleCheckLimitStatus = (data) => {
      if (!data || !data.limits) {
        return;
      }
      if (data.bingoName !== hallRef.current) {
        return;
      }
      if (
        parseInt(data.userID) !== userInfoRef.current.id &&
        parseInt(data.userID) !== parentInfoRef.current.id
      ) {
        return;
      }
      if (data.isTicket) {
        return;
      }

      updateUserLimits(data.limits);

      setTimeout(() => {
        setIsLoadingLimits(false);
      }, 500);

      if (data.isMonthly) {
        updateIsMonthlyLimitSurpassed(true);
        updateIsLimitSurpassed(true);
        updateShowIsLimitSurpassed(true);
        return;
      }
      if (data.isDaily) {
        updateIsDailyLimitSurpassed(true);
        updateIsLimitSurpassed(true);
        updateShowIsLimitSurpassed(true);
        return;
      }
    };

    socket.on('checkLimitStatus', handleCheckLimitStatus);

    return () => {
      socket.off('checkLimitStatus', handleCheckLimitStatus);
    };
  }, [socket]);

  const handleDone = () => {
    if (transactionsKioskRef.current.length === 0) {
      handleLogout();
      return;
    }
    updateShowModalPaymentKiosk(true);
  };

  const handleLogout = () => {
    if (transactionsKioskRef.current.length > 0) {
      updateShowModalConfirmLogout(true);
      return;
    }
    logoutUser();
    logoutKiosk();
    const hallToUse = hallRef.current || hall || 'Roulingo';
    navigate(`/login-kiosk?hall=${encodeURIComponent(hallToUse)}`, {
      replace: true,
    });
  };

  const isRefillTransaction = (transaction) =>
    (transaction?.description || '').toLowerCase().includes('fylle');

  const isPapirTransaction = (transaction) => {
    const haystack = (
      transaction?.description ||
      transaction?.name ||
      ''
    ).toLowerCase();
    return haystack.includes('papir');
  };

  const getKioskImageByName = (name = '') => {
    const normalizedName = name.toUpperCase();

    if (normalizedName.includes('CH')) {
      return clubhouseLogo;
    }
    if (normalizedName.includes('OK D')) {
      return okBingoLogo;
    }
    if (normalizedName.includes('WG')) {
      return wingamesLogo;
    }

    return clubhouseLogo;
  };

  const getKioskTransactionGroups = () => {
    if (!Array.isArray(transactionsKiosk)) {
      return [];
    }

    const grouped = {};

    transactionsKiosk.forEach((transaction, index) => {
      const amount = parseInt(transaction?.amount) || 0;
      const groupKey =
        isPapirTransaction(transaction) ? `papir__${index}` :
        isRefillTransaction(transaction) ? `refill__${index}` :
        `${transaction?.name || ''}__${amount}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          key: groupKey,
          transaction,
          amount,
          count: 0,
          indexes: [],
        };
      }

      grouped[groupKey].count += 1;
      grouped[groupKey].indexes.push(index);
    });

    return Object.values(grouped);
  };

  const handleKioskPlus = (groupItem) => {
    if (
      groupItem?.transaction?.name === 'Ny Billett - WG' ||
      groupItem?.transaction?.name === 'Ny Billett - OK D.'
    ) {
      return;
    }

    const currentTransactionsKiosk = Array.isArray(transactionsKioskRef.current)
      ? transactionsKioskRef.current
      : [];

    deleteTransactionsKiosk([
      ...currentTransactionsKiosk,
      { ...groupItem.transaction },
    ]);
  };

  const handleKioskMinus = (groupItem) => {
    const currentTransactionsKiosk = Array.isArray(transactionsKioskRef.current)
      ? transactionsKioskRef.current
      : [];

    if (!groupItem?.indexes?.length) {
      return;
    }

    if ((groupItem.count || 0) <= 1) {
      return;
    }

    const indexToRemove = groupItem.indexes[groupItem.indexes.length - 1];

    const updatedTransactionsKiosk = currentTransactionsKiosk.filter(
      (_, itemIndex) => itemIndex !== indexToRemove
    );

    deleteTransactionsKiosk(updatedTransactionsKiosk);
  };

  const handleKioskDelete = (groupItem) => {
    const currentTransactionsKiosk = Array.isArray(transactionsKioskRef.current)
      ? transactionsKioskRef.current
      : [];

    if (!groupItem?.indexes?.length) {
      return;
    }

    const indexesToDelete = new Set(groupItem.indexes);

    const updatedTransactionsKiosk = currentTransactionsKiosk.filter(
      (_, itemIndex) => !indexesToDelete.has(itemIndex)
    );

    deleteTransactionsKiosk(updatedTransactionsKiosk);
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          height: '100%',
          width: '100%',
          p: 1.5,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            borderRadius: '15px',
            display: 'grid',
            gridTemplateRows: '20% 1fr 20%', // info / list / totals
            rowGap: 1.5,
          }}
        >
          {/* 1) USER INFO (19%) ------------------------------------------ */}
          <Box
            sx={{
              px: 3,
              pb: 2,
              position: 'relative',
              display: 'flex',
              alignItems: 'space-evenly',
              justifyContent: 'space-evenly',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Clock — sits in the top-right corner, takes no layout space */}
            <Typography
              sx={{
                position: 'absolute',
                top: '-25px',
                fontSize: 16,
                fontWeight: 600,
                color: 'text.secondary',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '0.05em',
                textAlign: 'center',
                width: '100%',
                left: '-5px',
                marginBottom: '25px',
              }}
            >
              {time}
            </Typography>
            <Box
              sx={{
                bgcolor: '#121212',
                borderRadius: '15px',
                p: 3,
                width: '100%',
              }}
            >
              <Typography variant="h4" fontWeight={700}>
                {parentInfoRef.current.username
                  ? parentInfoRef.current.username
                  : userInfoRef.current.username
                    ? userInfoRef.current.username
                    : t('kiosk.guest')}
              </Typography>

              <Typography
                variant="subtitle1"
                color="text.secondary"
                mt={1}
                sx={{ fontSize: 20 }}
              >
                {hallRef.current ? hallRef.current : 'Bingo 1 Demo PROD'} |{' '}
                {userInfoRef.current.username !== 'Gjest'
                  ? userInfoRef.current.id
                  : '-'}
              </Typography>
            </Box>

            {/* Card 2: limits (converted from your <p> + <span> version) */}
            <Box
              sx={{
                bgcolor: '#121212',
                borderRadius: '15px',
                p: 3,
                width: '100%',
              }}
            >
              {userInfoRef.current.username &&
              userInfoRef.current.username === 'Gjest' ? (
                <>
                  {/* Daily limit */}
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: 20,
                      mb: 1,
                    }}
                  >
                    {t('sales.limitDaily')}:
                    <br />
                    <Box component="span" sx={{ fontSize: 25 }}>
                      -
                    </Box>{' '}
                    /{' '}
                    <Box component="span" sx={{ fontSize: 25 }}>
                      900
                    </Box>
                  </Typography>

                  {/* Monthly limit */}
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: 20,
                    }}
                  >
                    {t('sales.limitMonthly')}:
                    <br />
                    <Box component="span" sx={{ fontSize: 25 }}>
                      -
                    </Box>{' '}
                    /{' '}
                    <Box component="span" sx={{ fontSize: 25 }}>
                      4400
                    </Box>
                  </Typography>
                </>
              ) : (
                <>
                  {isLoadingLimits ? (
                    <>
                      {/* Daily */}
                      <Typography sx={{ fontSize: 20, mb: 1 }}>
                        <Skeleton
                          variant="text"
                          width={120}
                          height={16}
                          sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={70}
                            height={28}
                            sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
                          />
                          <Skeleton
                            variant="text"
                            width={16}
                            height={22}
                            sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                          />
                          <Skeleton
                            variant="text"
                            width={70}
                            height={28}
                            sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
                          />
                        </Box>
                      </Typography>

                      {/* Monthly */}
                      <Typography sx={{ fontSize: 20 }}>
                        <Skeleton
                          variant="text"
                          width={140}
                          height={16}
                          sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width={70}
                            height={28}
                            sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
                          />
                          <Skeleton
                            variant="text"
                            width={16}
                            height={22}
                            sx={{ bgcolor: 'rgba(255,255,255,0.12)' }}
                          />
                          <Skeleton
                            variant="text"
                            width={70}
                            height={28}
                            sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}
                          />
                        </Box>
                      </Typography>
                    </>
                  ) : (
                    userLimits.daily &&
                    userLimits.monthly && (
                      <>
                        {/* Daily limit */}
                        <Typography
                          sx={{
                            color:
                              userLimits.daily.limit >
                              userLimits.daily.cashIn - userLimits.daily.cashOut
                                ? 'white'
                                : '#fc86c1',
                            fontSize: 20,
                            mb: 1,
                          }}
                        >
                          {t('sales.limitDaily')}:
                          <br />
                          <Box component="span" sx={{ fontSize: 25 }}>
                            {userLimits.daily.cashIn - userLimits.daily.cashOut}
                          </Box>{' '}
                          /{' '}
                          <Box component="span" sx={{ fontSize: 25 }}>
                            {userLimits.daily.limit > 0 &&
                              userLimits.daily.limit}
                          </Box>
                        </Typography>

                        {/* Monthly limit */}
                        <Typography
                          sx={{
                            color:
                              userLimits.monthly.limit >
                              userLimits.monthly.totalSpent -
                                userLimits.monthly.rollover
                                ? 'white'
                                : '#fc86c1',
                            fontSize: 20,
                          }}
                        >
                          {t('sales.limitMonthly')}:
                          <br />
                          <Box component="span" sx={{ fontSize: 25 }}>
                            {userLimits.monthly.totalSpent -
                              userLimits.monthly.rollover}
                          </Box>{' '}
                          /{' '}
                          <Box component="span" sx={{ fontSize: 25 }}>
                            {userLimits.monthly.limit > 0 &&
                              userLimits.monthly.limit}
                          </Box>
                        </Typography>
                      </>
                    )
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* 3) ORDERS LIST (auto / rest of space) ------------------------ */}
          <Box
            ref={listRef}
            onPointerDown={handleListPointerDown}
            onPointerMove={handleListPointerMove}
            onPointerUp={endListDrag}
            onPointerLeave={endListDrag}
            onPointerCancel={endListDrag}
            sx={{
              py: 2,
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-y',
              overscrollBehavior: 'contain',
              cursor: 'grab',
              borderTop: '1px solid rgba(255,255,255,0.12)',
            }}
            className="hide-scrollbar"
          >
            <Typography variant="h4" fontWeight={700} mt={3} mb={2}>
              {t('kiosk.orderList')}
            </Typography>

            {getKioskTransactionGroups().length > 0 && (
              <>
                {getKioskTransactionGroups().map((groupItem) => {
                  const totalAmount = groupItem.amount * groupItem.count;

                  return (
                    <Box
                      key={groupItem.key}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        width: '100%',
                        py: 2.5,
                        px: 1.5,
                        mb: 1.75,
                        gap: 2.25,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.03)',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                      >
                        <Box
                          component="img"
                          src={getKioskImageByName(groupItem.transaction?.name)}
                          alt={groupItem.transaction?.name || 'kiosk item'}
                          sx={{
                            width: 64,
                            height: 40,
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                        />

                        <Typography
                          sx={{ m: 0, fontWeight: 600, fontSize: 25 }}
                        >
                          {groupItem.transaction?.name || '-'}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.35,
                        }}
                      >
                        <Typography sx={{ m: 0, fontSize: 27 }}>
                          {t('kiosk.price', { defaultValue: 'Price' })}:{' '}
                          {isPapirTransaction(groupItem.transaction)
                            ? groupItem.amount / (groupItem.transaction?.cards || 1)
                            : groupItem.amount}
                        </Typography>
                        <Typography sx={{ m: 0, fontSize: 27 }}>
                          {t('kiosk.totalPrice', {
                            defaultValue: 'Total price',
                          })}
                          : {totalAmount}
                        </Typography>
                        {isPapirTransaction(groupItem.transaction) ? (
                          <Typography sx={{ color: 'text.secondary', fontSize: 23 }}>
                            {t('kiosk.gameLabel')} {groupItem.transaction?.gameID}
                          </Typography>
                        ) : !isRefillTransaction(groupItem.transaction) && (
                          <Typography sx={{ color: 'text.secondary', fontSize: 23 }}>
                            {t('kiosk.tickets', { defaultValue: 'Tickets' })}:{' '}
                            {groupItem.count}
                          </Typography>
                        )}
                        {groupItem.transaction?.cutoffTime &&
                          (() => {
                            const remaining = Math.max(
                              0,
                              groupItem.transaction.cutoffTime - Date.now()
                            );
                            const mmss = new Date(remaining)
                              .toISOString()
                              .substring(14, 19);
                            const isUrgent = remaining < 60_000;
                            return (
                              <Typography
                                sx={{
                                  fontSize: 23,
                                  fontWeight: 700,
                                  color: isUrgent ? '#ff6b6b' : '#FFC727',
                                }}
                              >
                                {t('kiosk.cutoffRemaining')} {mmss}
                              </Typography>
                            );
                          })()}
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          justifyContent: 'space-evenly',
                          pt: 0.5,
                        }}
                      >
                        {!isPapirTransaction(groupItem.transaction) &&
                          !isRefillTransaction(groupItem.transaction) && (
                          <>
                            <Button
                              variant="outlined"
                              size="medium"
                              onClick={() => handleKioskPlus(groupItem)}
                              sx={{ minWidth: 52, px: 0.5, fontSize: 22 }}
                            >
                              +
                            </Button>
                            <Button
                              variant="outlined"
                              size="medium"
                              onClick={() => handleKioskMinus(groupItem)}
                              sx={{ minWidth: 52, px: 0.5, fontSize: 22 }}
                            >
                              -
                            </Button>
                          </>
                        )}

                        <Button
                          variant="outlined"
                          color="error"
                          size="medium"
                          onClick={() => handleKioskDelete(groupItem)}
                          sx={{ minWidth: 110, fontSize: 20 }}
                        >
                          {t('kiosk.delete', { defaultValue: 'Delete' })}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </>
            )}
          </Box>

          {/* 4) TOTAL + BUTTON (25%) -------------------------------------- */}
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 3,
              borderTop: '1px solid rgba(255,255,255,0.12)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {/* print row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography sx={{ m: 0, fontSize: 25 }}>
                {t('sales.print')}
              </Typography>
              <Checkbox
                checked={printReceiptKiosk}
                onChange={() => {
                  updatePrintReceiptKiosk(!printReceiptKiosk);
                }}
                sx={{
                  p: 0,
                  '& .MuiSvgIcon-root': { fontSize: 30 },
                }}
              />
            </Box>

            {/* total text */}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    lineHeight: 2,
                    color: '#5B5B5B',
                    fontSize: 31,
                    mb: 2,
                  }}
                >
                  {t('kiosk.total')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  lineHeight: 0.6,
                  color: '#FFFFFF',
                  fontSize: 48,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    lineHeight: 0.3,
                    color: '#FFFFFF',
                    fontSize: 48,
                    mb: 2,
                  }}
                >
                  {parseInt(total)}
                </Typography>
              </Box>
            </Box>

            {/* big yellow button */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDone}
                startIcon={<CheckCircleOutlineIcon />}
                sx={{
                  borderRadius: '15px',
                  height: '150px',
                  px: 7,
                  fontSize: 22,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  bgcolor: '#FFC727',
                  color: '#000000',
                  boxShadow: '0 16px 45px rgba(255, 199, 39, 0.45)',
                  transition:
                    'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
                  '& .MuiSvgIcon-root': {
                    fontSize: 45,
                  },
                  '&:hover': {
                    bgcolor: '#FFD54F',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 20px 55px rgba(255, 199, 39, 0.6)',
                  },
                }}
              >
                {t('kiosk.done')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Grid>
    </>
  );
}

export default TransactionList;
